import { ensurePurchaseLineIds, newPurchaseLine } from '../purchaseLines';
import PermissionButton from '../../../permissions/PermissionButton';
import React, { useEffect, useState } from 'react';
import { Card, Descriptions, Space, Button, Tag, Select, message, Drawer } from 'antd';
import NestedAchatTable from '../NestedAchatTable';
import AddItemToAvoirAchatForm from './AddItemToAvoirAchatForm';
import { useFetchAvoirAchatById, useFetchItemsInAvoirAchat, useValidateAvoirAchat, useUnvalidateAvoirAchat, useSaveAvoirAchat } from '../../../hooks/achatAvoirHooks';
import { useFetchAllEntrepots, useDefaultEntrepot } from '../../../hooks/entrepotHooks';
import EditableDateField from '../../../utils/EditableDateField';
import { formatAmount } from '../../../utils/formatAmount';

const SingleAvoirAchat = ({ id, onStatusChange, onClose }) => {
  const { data, loading, refetch } = useFetchAvoirAchatById(id);
  const { items, loading: itemsLoading, refetch: refetchItems } = useFetchItemsInAvoirAchat(id);
  const { validate } = useValidateAvoirAchat();
  const { unvalidate } = useUnvalidateAvoirAchat();
  const { save } = useSaveAvoirAchat();
  const { entrepots } = useFetchAllEntrepots();
  const { defaultCode } = useDefaultEntrepot();

  const [localAvoir, setLocalAvoir] = useState(null);
  const [localItems, setLocalItems] = useState([]);
  const [hasLocalChanges, setHasLocalChanges] = useState(false);
  const [isAddItemDrawerVisible, setIsAddItemDrawerVisible] = useState(false);

  useEffect(() => { if (data) setLocalAvoir({ ...data }); }, [data]);
  useEffect(() => { if (items) setLocalItems(ensurePurchaseLineIds(items || [])); }, [items]);

  // Prefill default entrepot when avoir achat has none (editable only)
  useEffect(() => {
    if (!localAvoir || !defaultCode) return;
    if (localAvoir.VALIDER) return;
    if (localAvoir.CODE_ENT) return;

    setLocalAvoir(prev => (prev && !prev.CODE_ENT ? { ...prev, CODE_ENT: defaultCode } : prev));
    setHasLocalChanges(true);
  }, [
    defaultCode,
    localAvoir?.id,
    localAvoir?.CODE_ENT,
    localAvoir?.VALIDER,
  ]);

  // Calculate totals whenever items change
  useEffect(() => {
    if (localItems && localAvoir) {
      const totals = localItems.reduce((acc, item) => {
        const qte = item.QTE || 0;
        const paHT = item.PA_HT || 0;
        const paTTC = item.PA_TTC || (paHT * (1 + (item.TVA || 0) / 100));
        const remise = item.REMISE || 0;
        const totalTTC = item.TotalTTC ?? (qte * paTTC * (1 - remise / 100));
        const totalHT = item.TotalHT ?? (qte * paHT * (1 - remise / 100));
        return {
          totalTTC: acc.totalTTC + totalTTC,
          totalHT: acc.totalHT + totalHT
        };
      }, { totalTTC: 0, totalHT: 0 });

      setLocalAvoir(prev => ({
        ...prev,
        MNT_TTC: -totals.totalTTC,  // Negative for avoir
        MNT_HT: -totals.totalHT     // Negative for avoir
      }));
    }
  }, [localItems]);

  const handleSave = (row) => {
    setLocalItems((prevItems) =>
      prevItems.map((item) =>
        item.id === row.id ? { ...item, ...row } : item
      )
    );
    setHasLocalChanges(true);
    message.success("Article modifié localement. Cliquez sur 'Enregistrer' pour sauvegarder.");
  };

  const handleDelete = (lineId) => {
    setLocalItems((prevItems) => prevItems.filter(item => item.id !== lineId));
    setHasLocalChanges(true);
    message.success("Article supprimé localement. Cliquez sur 'Enregistrer' pour sauvegarder.");
  };

  const handleAddItemSuccess = (payload) => {
    const itemsToAdd = Array.isArray(payload) ? payload : [payload];
    setLocalItems((prevItems) => {
      const updated = [...prevItems];
      itemsToAdd.forEach((newItem) => {
        if (!newItem || !newItem.CODE_ART) return;
        updated.push(newPurchaseLine(newItem));
      });
      return updated;
    });
    setHasLocalChanges(true);
    setIsAddItemDrawerVisible(false);
    message.success(itemsToAdd.length > 1 ? `${itemsToAdd.length} articles ajoutés` : 'Article ajouté');
  };

  const doValidate = async () => { await validate(id); await refetch(); await refetchItems?.(); onStatusChange && onStatusChange(); };
  const doUnvalidate = async () => { await unvalidate(id); await refetch(); await refetchItems?.(); onStatusChange && onStatusChange(); };

  const handleSaveChanges = async () => {
    try {
      const details = (localItems || []).map(({ article, ...it }) => ({ ...it }));
      await save(localAvoir, details);
      setHasLocalChanges(false);
      message.success('Avoir achat enregistré');
      await refetch();
      await refetchItems?.();
    } catch (e) {
      message.error(e?.message || 'Échec de l\'enregistrement');
    }
  };

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <Card title={
        <>
          Avoir achat {localAvoir?.REF_AVR_A || ''}
          <Tag color={localAvoir?.CODE_ENT ? 'blue' : 'default'} style={{ marginLeft: 8 }}>
            Entrepôt: {localAvoir?.CODE_ENT 
              ? (localAvoir.entrepot ? `${localAvoir.entrepot.code_ent} - ${localAvoir.entrepot.name}` : localAvoir.CODE_ENT)
              : 'Non défini'}
          </Tag>
        </>
      } loading={loading} extra={
        <Space>
          {!localAvoir?.VALIDER && (
            <>
              <PermissionButton permissionModule="purchaseReturns" permissionAction="update" type="primary" onClick={handleSaveChanges} disabled={!hasLocalChanges}>Enregistrer</PermissionButton>
              <PermissionButton permissionModule="purchaseReturns" permissionAction="validate" type="primary" onClick={doValidate} disabled={hasLocalChanges}>Valider</PermissionButton>
            </>
          )}
          {localAvoir?.VALIDER && (
            <PermissionButton permissionModule="purchaseReturns" permissionAction="unvalidate" danger onClick={doUnvalidate}>Dévalider</PermissionButton>
          )}
          {onClose && <Button onClick={onClose}>Fermer</Button>}
        </Space>
      }>
        <Descriptions bordered size="small" column={2}>
          <Descriptions.Item label="Date">
            <EditableDateField
              value={localAvoir?.DATE_AVR}
              onChange={(newDate) => {
                setLocalAvoir((prev) => ({ ...prev, DATE_AVR: newDate }));
                setHasLocalChanges(true);
              }}
              disabled={!!localAvoir?.VALIDER}
            />
          </Descriptions.Item>
          <Descriptions.Item label="Fournisseur">{localAvoir?.FOURNISSEUR}</Descriptions.Item>
          <Descriptions.Item label="Montant HT">{formatAmount(Math.abs(localAvoir?.MNT_HT || 0))}</Descriptions.Item>
          <Descriptions.Item label="Montant TTC">{formatAmount(Math.abs(localAvoir?.MNT_TTC || 0))}</Descriptions.Item>
          <Descriptions.Item label="Entrepôt" span={1}>
            <Space size={6}>
              <Tag color={localAvoir?.CODE_ENT ? 'blue' : 'default'}>
                {localAvoir?.CODE_ENT 
                  ? (localAvoir.entrepot ? `${localAvoir.entrepot.code_ent} - ${localAvoir.entrepot.name}` : localAvoir.CODE_ENT)
                  : 'Non défini'}
              </Tag>
              {!localAvoir?.VALIDER && (
                <Select
                  size="small"
                  style={{ minWidth: 200 }}
                  placeholder="Sélectionner un entrepôt"
                  value={localAvoir?.CODE_ENT}
                  onChange={(value) => {
                    setLocalAvoir(prev => ({ ...prev, CODE_ENT: value }));
                    setHasLocalChanges(true);
                  }}
                  options={(entrepots || []).map(e => ({ value: e.code_ent, label: `${e.code_ent} - ${e.name}` }))}
                />
              )}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Remarque" span={1}>{localAvoir?.REMARQUE || ''}</Descriptions.Item>
        </Descriptions>
      </Card>
      <Card 
        title="Articles" 
        loading={itemsLoading}
        extra={
          !localAvoir?.VALIDER && (
            <PermissionButton permissionModule="purchaseReturns" permissionAction="update"
              onClick={() => setIsAddItemDrawerVisible(true)}
              type="primary"
            >
              Ajouter Article
            </PermissionButton>
          )
        }
      >
        <NestedAchatTable
          items={localItems || []}
          handleSave={handleSave}
          handleDelete={handleDelete}
          validated={!!localAvoir?.VALIDER}
          userPermissions={{ isManager: true }}
          type="avoir"
          codeEnt={localAvoir?.CODE_ENT}
        />
      </Card>

      <Drawer
        title="Ajouter un article à l'avoir achat"
        width={900}
        onClose={() => setIsAddItemDrawerVisible(false)}
        open={isAddItemDrawerVisible}
      >
        <AddItemToAvoirAchatForm
          refAvoir={localAvoir?.REF_AVR_A}
          onSuccess={(newItem) => {
            handleAddItemSuccess(newItem);
            setIsAddItemDrawerVisible(false);
          }}
        />
      </Drawer>
    </div>
  );
};

export default SingleAvoirAchat;

